import type { TFunction } from 'i18next';
import { Link } from 'react-router-dom';
import { formatClock } from '@/features/trips/format';
import { tamperPin } from './types';
import type { TamperAlert } from './types';

/**
 * A TAMPER alert in the alerts feed (slice 55) — the EIGHTH item shape, beside the
 * geofence crossing, the overspeed episode, the maintenance reminder, the
 * tracker-health event, the after-hours use, the power event and the ignition event.
 *
 * ONE TONE: tamper is ALWAYS `critical` (the frame reported vibration while the
 * vehicle was parked — anti-theft). There is no quiet variant, so it always wears
 * the attention styling a critical row uses: the error-toned tag and the accent
 * unread dot. It is the loud one, by construction.
 *
 * 🔴 LOCATION IS THE LAST KNOWN FIX, NOT WHERE IT HAPPENED, AND IT MAY NOT EXIST.
 * A tamper frame's own position is only trustworthy when it was a valid fix, so the
 * pin — when there is one — is the vehicle's last VALID GPS fix, and it is LABELLED
 * "last known location" so it is never read as the spot the vehicle was shaken. A
 * device that never got a fix has none, and the row must not invent one from live
 * state (position 27). This mirrors `PowerRow` exactly:
 *   - a tamper WITH a pin → a select button: selecting it centres the fleet map on
 *     the last-known fix (and marks it read); on vehicle detail (no map) selecting
 *     just marks it read.
 *   - a tamper WITHOUT a pin → a `Link` straight to the vehicle, the honest
 *     destination for a location-less event. No map-select button onto nothing.
 *
 * `tamperPin` is the ONLY reader of the wire coordinates, so a rename of the wire
 * fields is a one-line change there, never a hunt across this file.
 *
 * Read/unread is a leading dot + a weight change, never colour alone. Tokens only;
 * no hex literal enters this file.
 */
export function TamperRow({
  alert,
  plate,
  locale,
  timeZone,
  selected,
  onSelect,
  t,
}: {
  alert: TamperAlert;
  plate?: string | null;
  locale: string;
  timeZone: string;
  selected: boolean;
  onSelect: (alert: TamperAlert) => void;
  t: TFunction;
}) {
  const pin = tamperPin(alert);
  const observedTime = formatClock(locale, Date.parse(alert.observedAt), timeZone);

  const inner = (
    <div className="flex items-start gap-2.5">
      <span
        aria-hidden="true"
        className={[
          'mt-1.5 size-2 flex-none rounded-full',
          alert.read ? 'bg-transparent' : 'bg-raad-accent',
        ].join(' ')}
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span
            className={[
              'truncate text-sm',
              alert.read ? 'font-medium text-raad-muted' : 'font-semibold text-raad-text',
            ].join(' ')}
          >
            {t('alerts.tamper.title')}
          </span>
          <span
            data-testid="tamper-tag"
            className="flex-none rounded-md bg-raad-error/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-raad-error"
          >
            {t('alerts.tamper.tag')}
          </span>
          {!alert.read ? <span className="sr-only">{t('alerts.unread')}</span> : null}
        </div>

        {plate ? (
          <div className="mt-0.5 truncate text-[11.5px] font-semibold text-raad-dim">{plate}</div>
        ) : null}

        <div className="mt-1 text-[12px] text-raad-muted">
          {t('alerts.observedAt', { time: observedTime })}
        </div>

        {/* The last-known location line: with a pin it is the affordance ("selecting
            this shows the last known location on the map"); without one it states,
            honestly, that there is no fix to show. */}
        {pin ? (
          <div
            data-testid="tamper-last-known"
            className="mt-1 flex items-center gap-1.5 text-[12px] font-semibold text-raad-accent"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M12 21s-7-6.3-7-11a7 7 0 0 1 14 0c0 4.7-7 11-7 11Z" />
              <circle cx="12" cy="10" r="2.5" />
            </svg>
            {t('alerts.tamper.lastKnownLocation')}
          </div>
        ) : (
          <div data-testid="tamper-no-location" className="mt-1 text-[12px] text-raad-dim">
            {t('alerts.tamper.noLocation')}
          </div>
        )}
      </div>
    </div>
  );

  const containerClass = [
    'block w-full rounded-xl border px-3.5 py-3 text-left transition-colors',
    selected
      ? 'border-raad-primary/40 bg-raad-primary/10'
      : 'border-raad-line-7 bg-raad-tile hover:border-raad-line-15',
  ].join(' ');

  return (
    <li>
      {pin ? (
        // Positioned: selecting centres the fleet map on the last-known fix (and
        // marks read), exactly like PowerRow / OverspeedRow.
        <button
          type="button"
          onClick={() => onSelect(alert)}
          aria-pressed={selected}
          data-testid="tamper-row"
          data-type={alert.type}
          data-severity={alert.severity}
          data-alert-id={alert.id}
          data-has-pin="true"
          className={containerClass}
        >
          {inner}
        </button>
      ) : (
        // Location-less: a Link to the vehicle — the honest destination — never a
        // map-select button onto nothing.
        <Link
          to={`/vehicles/${alert.vehicleId}`}
          data-testid="tamper-row"
          data-type={alert.type}
          data-severity={alert.severity}
          data-alert-id={alert.id}
          data-has-pin="false"
          className={containerClass}
        >
          {inner}
        </Link>
      )}
    </li>
  );
}
